import { api } from "@/features/utils/fetch-server";
import type {
  CalDAVSetupData,
  CreateExternalAccountData,
  ExternalAccount,
  RenewAccessResult,
  UpdateExternalAccountData,
  VerifyAccessResult,
} from "../types";

export class ExternalAccountsAPI {
  static getAccounts = async (clientId: string) => {
    const response = await api.get<{
      accounts: ExternalAccount[];
    }>(`/api/accounts/v1/${clientId}/external-accounts`);
    return response.accounts;
  };

  static getAccount = async (clientId: string, id: string) => {
    const response = await api.get<{
      account: ExternalAccount;
    }>(`/api/accounts/v1/${clientId}/external-accounts/${id}`);
    return response.account;
  };

  static createAccount = async (
    clientId: string,
    data: CreateExternalAccountData,
  ) => {
    const response = await api.post<
      { account: ExternalAccount },
      CreateExternalAccountData
    >(`/api/accounts/v1/${clientId}/external-accounts`, data);
    return response.account;
  };

  static updateAccount = async (
    clientId: string,
    id: string,
    data: UpdateExternalAccountData,
  ) => {
    const response = await api.put<
      {
        account: ExternalAccount;
      },
      UpdateExternalAccountData
    >(`/api/accounts/v1/${clientId}/external-accounts/${id}`, data);
    return response.account;
  };

  static deleteAccount = async (clientId: string, id: string) => {
    await api.delete(`/api/accounts/v1/${clientId}/external-accounts/${id}`);
  };

  static verifyAccess = async (clientId: string, id: string) => {
    const response = await api.post<VerifyAccessResult, void>(
      `/api/accounts/v1/${clientId}/external-accounts/${id}/verify`,
    );
    return response;
  };

  static renewAccess = async (clientId: string, id: string) => {
    const response = await api.post<RenewAccessResult, void>(
      `/api/accounts/v1/${clientId}/external-accounts/${id}/renew`,
    );
    return response;
  };

  static getAuthorizationUrl = async (
    clientId: string,
    provider: "google" | "microsoft",
    capabilities: string[],
  ): Promise<string> => {
    const response = await api.get<{
      authorization_url: string;
    }>(
      `/api/accounts/v1/${clientId}/oauth/authorize/${provider}?capabilities=${capabilities.join(",")}`,
    );
    return response.authorization_url;
  };

  static setupCalDAV = async (clientId: string, data: CalDAVSetupData) => {
    const response = await api.post<
      {
        success: boolean;
        account: { id: string; name: string };
      },
      CalDAVSetupData
    >(`/api/accounts/v1/${clientId}/dav/setup`, data);
    return response;
  };

  static setupICS = async (
    clientId: string,
    data: { url: string; name: string },
  ) => {
    const response = await api.post<
      {
        success: boolean;
        account: { id: string; name: string };
      },
      { url: string; name: string }
    >(`/api/accounts/v1/${clientId}/ics/setup`, data);
    return response;
  };

  static getSettings = async (clientId: string) => {
    const response = await api.get<{
      settings: import("../types").ExternalAccountSettings | null;
    }>(`/api/accounts/v1/${clientId}/settings`);
    return response.settings;
  };

  static updateSettings = async (
    clientId: string,
    settings: Partial<import("../types").ExternalAccountSettings> & {
      primary_calendar_create?: { account_id: string; name: string };
      contacts_addressbook_create?: { account_id: string; name: string };
    },
  ) => {
    const response = await api.put<
      { settings: import("../types").ExternalAccountSettings },
      Partial<import("../types").ExternalAccountSettings> & {
        primary_calendar_create?: { account_id: string; name: string };
        contacts_addressbook_create?: { account_id: string; name: string };
      }
    >(`/api/accounts/v1/${clientId}/settings`, settings);
    return response.settings;
  };

  static listCalendars = async (clientId: string) => {
    const response = await api.get<{
      calendars: (import("../types").RemoteCalendar & {
        account_id: string;
        account_name: string;
        account_provider: string;
      })[];
    }>(`/api/accounts/v1/${clientId}/calendars`);
    return response.calendars;
  };

  static listAddressbooks = async (clientId: string) => {
    const response = await api.get<{
      addressbooks: (import("../types").RemoteAddressbook & {
        account_id: string;
        account_name: string;
        account_provider: string;
      })[];
    }>(`/api/accounts/v1/${clientId}/addressbooks`);
    return response.addressbooks;
  };
}
